import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EmailVerificationNotice = ({ userEmail, onResendVerification }) => {
  const [isResending, setIsResending] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [lastResendTime, setLastResendTime] = useState(null);

  const handleResendVerification = async () => {
    setIsResending(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setResendCount(prev => prev + 1);
      setLastResendTime(new Date());
      onResendVerification?.(userEmail);
    } catch (error) {
      console.error('Failed to resend verification:', error);
    } finally {
      setIsResending(false);
    }
  };

  const canResend = !isResending && (!lastResendTime || Date.now() - lastResendTime > 60000);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center space-y-6">
        {/* Success Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
            <Icon name="Mail" size={32} color="var(--color-success)" />
          </div>
        </div>

        {/* Main Message */}
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">
            Check Your Email
          </h3>
          <p className="text-muted-foreground">
            We've sent a verification link to
          </p>
          <p className="text-lg font-semibold text-foreground break-all">
            {userEmail}
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <h4 className="font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Info" size={16} />
            <span>Next Steps:</span>
          </h4>
          <ol className="text-sm text-muted-foreground space-y-2 text-left">
            <li className="flex items-start space-x-2">
              <span className="font-medium text-primary">1.</span>
              <span>Check your email inbox (and spam folder)</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-medium text-primary">2.</span>
              <span>Click the verification link in the email</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="font-medium text-primary">3.</span>
              <span>Return here to complete your registration</span>
            </li>
          </ol>
        </div>

        {/* Resend Section */}
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Didn't receive the email?
            </p>
            
            {resendCount > 0 && (
              <div className="mb-3 p-3 bg-success/10 border border-success/20 rounded-lg">
                <p className="text-sm text-success">
                  ✓ Verification email sent successfully!
                </p>
                {resendCount > 1 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Sent {resendCount} times
                  </p>
                )}
              </div>
            )}

            <Button
              variant="outline"
              onClick={handleResendVerification}
              disabled={!canResend}
              loading={isResending}
              iconName="RefreshCw"
              iconPosition="left"
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </Button>

            {!canResend && !isResending && (
              <p className="text-xs text-muted-foreground mt-2">
                Please wait 60 seconds before requesting another email
              </p>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="border-t border-border pt-6">
          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Still having trouble?
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button variant="ghost" size="sm">
                Contact Support
              </Button>
              <Button variant="ghost" size="sm">
                Check Email Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Email Provider Tips */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h5 className="font-medium text-foreground mb-2 flex items-center space-x-2">
            <Icon name="Lightbulb" size={16} />
            <span>Email Tips:</span>
          </h5>
          <ul className="text-xs text-muted-foreground space-y-1 text-left">
            <li>• Check your spam or junk folder</li>
            <li>• Add noreply@DevOra.com to your contacts</li>
            <li>• Make sure your email client allows images</li>
            <li>• The verification link expires in 24 hours</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationNotice;